/**
 * Map tiles stay on api.mapbox.com. Usage events must not reach events.mapbox.com.
 * EVENTS_URL is a getter derived from API_URL in Mapbox GL JS 3, so it is replaced.
 */

const EVENTS_HOST = 'events.mapbox.com';

type ConfigWithEvents = {
  EVENTS_URL?: string | null;
};

export function disableMapboxEventsUrl(config: object): boolean {
  try {
    Object.defineProperty(config, 'EVENTS_URL', {
      configurable: true,
      enumerable: true,
      get: () => undefined,
    });
    return !(config as ConfigWithEvents).EVENTS_URL;
  } catch (error) {
    console.warn('Mapbox telemetry URL could not be replaced', error);
    return false;
  }
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

/** Drop events.mapbox.com before the browser opens a connection. */
export function installMapboxEventsBlock(): void {
  if (typeof window === 'undefined') return;
  const flagged = window as Window & {
    __citysageBlockedMapboxEvents?: boolean;
  };
  if (flagged.__citysageBlockedMapboxEvents) return;
  flagged.__citysageBlockedMapboxEvents = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    if (requestUrl(input).includes(EVENTS_HOST)) {
      return Promise.resolve(new Response(null, { status: 204 }));
    }
    return originalFetch(input, init);
  };

  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function open(
    this: XMLHttpRequest,
    ...args: unknown[]
  ) {
    if (String(args[1] ?? '').includes(EVENTS_HOST)) {
      args[1] = 'about:blank';
    }
    return originalOpen.apply(this, args as Parameters<XMLHttpRequest['open']>);
  } as typeof XMLHttpRequest.prototype.open;

  if (
    typeof navigator !== 'undefined' &&
    typeof navigator.sendBeacon === 'function'
  ) {
    const originalSendBeacon = navigator.sendBeacon.bind(navigator);
    try {
      Object.defineProperty(navigator, 'sendBeacon', {
        configurable: true,
        value: (url: string | URL, data?: BodyInit | null) => {
          if (String(url).includes(EVENTS_HOST)) return true;
          return originalSendBeacon(url, data);
        },
      });
    } catch {
      // Some browsers expose sendBeacon as non-configurable.
    }
  }
}

export function disableMapboxTelemetry(mapbox: { config?: object }): void {
  installMapboxEventsBlock();
  if (mapbox.config) {
    disableMapboxEventsUrl(mapbox.config);
  }
}
